// Magic-link auth via Supabase. The 10-person team gets an email with a
// one-click sign-in link; no passwords. Sessions persist in localStorage.
//
// Roles live in `profiles.role` (viewer | contributor | approver | editor)
// and are NOT in the JWT — we read them on demand. Cheap; the row is small
// and reads are cached by Supabase's PostgREST layer.

import { sb } from "./db.ts";

export type Role = "viewer" | "contributor" | "approver" | "editor";

export interface SessionInfo {
  email: string | null;
  user_id: string | null;
  display_name: string | null;
  initials: string | null;
  role: Role;
}

export async function getSession(): Promise<SessionInfo> {
  const {
    data: { session },
  } = await sb.auth.getSession();
  if (!session?.user) {
    return { email: null, user_id: null, display_name: null, initials: null, role: "viewer" };
  }
  const { data: profile } = await sb
    .from("profiles")
    .select("display_name, initials, role")
    .eq("id", session.user.id)
    .maybeSingle();
  return {
    email: session.user.email ?? null,
    user_id: session.user.id,
    display_name: profile?.display_name ?? session.user.email ?? null,
    initials: profile?.initials ?? null,
    role: (profile?.role as Role | undefined) ?? "viewer",
  };
}

/** Send a magic-link sign-in email. Returns when the email is queued. */
export async function signInWithEmail(email: string): Promise<void> {
  const { error } = await sb.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin },
  });
  if (error) throw new Error(error.message);
}

export async function signOut(): Promise<void> {
  await sb.auth.signOut();
}

/** Subscribe to session changes — call once on app boot to keep the topbar in sync. */
export function onAuthChange(cb: (s: SessionInfo) => void): () => void {
  const { data: sub } = sb.auth.onAuthStateChange(async () => {
    cb(await getSession());
  });
  return () => sub.subscription.unsubscribe();
}
